import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { apiFetchJson } from "../api";
import { UserIcon, XIcon, FileIcon, EditIcon, SendIcon, Spinner, PlusIcon, UploadIcon, GlobeIcon, CheckIcon, ChevronRightIcon } from "./Icons";
import { t, tf } from "../i18n";
import { aa } from "../theme/palette";
import { lockBodyScroll, unlockBodyScroll, useModalTouchGuard } from "../utils/bodyScrollLock";
import { overlayMotionProps, dialogMotionProps } from "../utils/modalPresence";
import { useEscapeLayer } from "../utils/escapeLayers";
import StyledSelect from "./StyledSelect";

// Пределы высоты списка прокси: не выше 240, и не ниже 140 — меньше уже не
// выбрать, лучше докрутить модалку.
const DROPDOWN_MAX_HEIGHT = 240;
const DROPDOWN_MIN_HEIGHT = 140;

// Встроенные прокси — одной строкой «Umbrella», без выбора из пула: какой
// прокси достанется аккаунту, решает сервер — следующий по кругу (umbrella-auto,
// см. resolveRequestedProxyId в backend/src/index.js).
const BUILTIN_PROXY_AUTO_ID = "umbrella-auto";
const collapseBuiltinProxies = (list) => {
  const items = Array.isArray(list) ? list : [];
  const pool = items.filter((proxy) => proxy?.isBuiltin);
  if (!pool.length) return items;
  const anyAlive = pool.some((proxy) => String(proxy?.status || "") !== "failed");
  return [
    { id: BUILTIN_PROXY_AUTO_ID, name: "Umbrella", isBuiltin: true, status: anyAlive ? "active" : "failed", checkResult: null },
    ...items.filter((proxy) => !proxy?.isBuiltin)
  ];
};

const AddAccountModal = ({ isOpen, dialogState = "open", isPhoneView = false, onClose, onSuccess, onBulkSuccess, proxies }) => {
  // Пока окно открыто, страница под ним не прокручивается — иначе на телефоне
  // жест на короткой форме уезжал в фон.
  useEffect(() => {
    if (!isOpen) return undefined;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [isOpen]);
  const overlayRef = useRef(null);
  useModalTouchGuard(overlayRef, isOpen && isPhoneView);
  const [loading, setLoading] = useState(false);
  const [selectedProxy, setSelectedProxy] = useState("");
  const [files, setFiles] = useState([]);
  const [cookieText, setCookieText] = useState("");
  const [accountAddMode, setAccountAddMode] = useState("cookies");
  // По умолчанию — текст: cookies чаще вставляют, чем загружают файлом.
  const [uploadMode, setUploadMode] = useState("text");
  const [registrationForm, setRegistrationForm] = useState({
    email: "",
    password: "",
    contactName: "",
    accountType: "private",
    confirmationUrl: "",
    phoneNationalNumber: "",
    phoneCode: ""
  });
  const [registrationSessionId, setRegistrationSessionId] = useState("");
  const [registrationStage, setRegistrationStage] = useState("details");
  const [registrationNotice, setRegistrationNotice] = useState("");
  const [detectingAccount, setDetectingAccount] = useState(false);
  const [detectedAccount, setDetectedAccount] = useState(null);
  const [detectError, setDetectError] = useState("");
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [availableProxies, setAvailableProxies] = useState([]);
  const [proxyDropdownOpen, setProxyDropdownOpen] = useState(false);
  // Escape: сначала список прокси, если раскрыт, потом само окно.
  useEscapeLayer(isOpen && dialogState !== "closed", () => onClose());
  useEscapeLayer(isOpen && proxyDropdownOpen, () => setProxyDropdownOpen(false));
  // Где стоит список прокси. Внутри карточки он обрезался её краем: колонка
  // формы прокручивается, карточка срезает всё, что за краем, и нижние прокси
  // было не достать, не прокрутив всю модалку. Теперь список рисуется поверх
  // карточки (в слое подложки, position: fixed) по координатам поля: вниз, если
  // снизу хватает экрана, иначе вверх, высотой не больше, чем помещается.
  const [proxyDropdownPlacement, setProxyDropdownPlacement] = useState(null);
  const proxyDropdownRef = useRef(null);
  const proxyDropdownListRef = useRef(null);
  const detectRequestSeqRef = useRef(0);
  const submitInFlightRef = useRef(false);
  // Поле выбора файлов и подсветка, пока файлы тянут на карточку.
  const fileInputRef = useRef(null);
  const [fileDropOver, setFileDropOver] = useState(false);

  useEffect(() => {
    setAvailableProxies(collapseBuiltinProxies(proxies));
  }, [proxies]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const loadProxies = async () => {
      try {
        const data = await apiFetchJson("/api/proxies");
        if (!cancelled) {
          setAvailableProxies(collapseBuiltinProxies(data));
        }
      } catch (error) {
        if (!cancelled) {
          setAvailableProxies(collapseBuiltinProxies(proxies));
        }
      }
    };

    loadProxies();

    return () => {
      cancelled = true;
    };
  }, [isOpen, proxies]);

  useEffect(() => {
    if (!selectedProxy) return;
    const stillExists = availableProxies?.some((proxy) => String(proxy.id) === String(selectedProxy));
    if (!stillExists) {
      setSelectedProxy("");
    }
  }, [availableProxies, selectedProxy]);

  // По умолчанию — встроенный прокси «Umbrella», если он есть и рабочий: без
  // прокси имя аккаунта по cookies не определяется (показывался «ID …»), а
  // добавление всё равно требует прокси. Подставляется один раз за открытие
  // окна — выбор пользователя не перебивается.
  const builtinAutoPickedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      builtinAutoPickedRef.current = false;
      return;
    }
    if (builtinAutoPickedRef.current || selectedProxy) return;
    const builtin = availableProxies.find((proxy) => String(proxy?.id) === BUILTIN_PROXY_AUTO_ID && String(proxy?.status || "") !== "failed");
    if (!builtin) return;
    builtinAutoPickedRef.current = true;
    setSelectedProxy(String(builtin.id));
  }, [isOpen, availableProxies, selectedProxy]);

  useEffect(() => {
    if (isOpen) return;
    setLoading(false);
    setSelectedProxy("");
    setFiles([]);
    setCookieText("");
    setAccountAddMode("cookies");
    setUploadMode("text");
    setRegistrationForm({
      email: "",
      password: "",
      contactName: "",
      accountType: "private",
      confirmationUrl: "",
      phoneNationalNumber: "",
      phoneCode: ""
    });
    setRegistrationSessionId("");
    setRegistrationStage("details");
    setRegistrationNotice("");
    setProxyDropdownOpen(false);
    setDetectingAccount(false);
    setDetectedAccount(null);
    setDetectError("");
    setUploadProgress({ current: 0, total: 0 });
    setFileDropOver(false);
    detectRequestSeqRef.current += 1;
    submitInFlightRef.current = false;
  }, [isOpen]);

  const extractJsonArraySegments = (rawValue) => {
    const text = String(rawValue || "");
    if (!text.includes("[") || !text.includes("]")) return [];
    const segments = [];
    let start = -1;
    let depth = 0;
    let inString = false;
    let escaped = false;

    for (let index = 0; index < text.length; index += 1) {
      const char = text[index];
      if (inString) {
        if (escaped) {
          escaped = false;
          continue;
        }
        if (char === "\\") {
          escaped = true;
          continue;
        }
        if (char === "\"") {
          inString = false;
        }
        continue;
      }
      if (char === "\"") {
        inString = true;
        continue;
      }
      if (char === "[") {
        if (depth === 0) {
          start = index;
        }
        depth += 1;
        continue;
      }
      if (char === "]" && depth > 0) {
        depth -= 1;
        if (depth === 0 && start >= 0) {
          segments.push(text.slice(start, index + 1));
          start = -1;
        }
      }
    }
    return segments;
  };

  const normalizeCookiePayload = (rawValue) => {
    const text = String(rawValue || "").replace(/\u0000/g, "").trim();
    if (!text) return "";

    // 1) Try to extract a JSON array (cookie export format)
    const jsonSegments = extractJsonArraySegments(text);
    if (jsonSegments.length) {
      // Pick the largest JSON array — likely the cookies
      let best = jsonSegments[0];
      for (const seg of jsonSegments) {
        if (seg.length > best.length) best = seg;
      }
      return best.trim();
    }

    // 2) Fallback: bracket slice (first '[' to last ']')
    const firstBracket = text.indexOf("[");
    const lastBracket = text.lastIndexOf("]");
    if (firstBracket >= 0 && lastBracket > firstBracket) {
      const slice = text.slice(firstBracket, lastBracket + 1).trim();
      // Only use if it looks like valid JSON array start
      if (/^\[\s*\{/.test(slice)) return slice;
    }

    // 3) Extract cookie-like lines from garbage text
    const lines = text.split(/\r?\n/);
    const cookieLines = [];
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      // Netscape format: domain TAB flag TAB path TAB secure TAB expires TAB name TAB value
      if (trimmed.split("\t").length >= 7) { cookieLines.push(trimmed); continue; }
      // Set-Cookie header
      if (/^set-cookie:/i.test(trimmed)) { cookieLines.push(trimmed); continue; }
      // Cookie header line
      if (/^cookie:/i.test(trimmed)) { cookieLines.push(trimmed); continue; }
      // key=value pairs with semicolons (cookie header style)
      if (trimmed.includes("=") && trimmed.includes(";") && /;\s*[^=;\s]+=/.test(trimmed)) { cookieLines.push(trimmed); continue; }
      // Simple key=value (potential cookie pair)
      if (/^[a-zA-Z_][a-zA-Z0-9_.~-]*=/.test(trimmed) && !trimmed.includes(" ")) { cookieLines.push(trimmed); continue; }
    }
    if (cookieLines.length > 0) return cookieLines.join("\n");

    return text;
  };

  const splitCookieTextEntries = (rawValue) => {
    const raw = String(rawValue || "").replace(/\u0000/g, "");
    const arrays = extractJsonArraySegments(raw)
      .map((segment) => normalizeCookiePayload(segment))
      .filter(Boolean);
    if (arrays.length) {
      return Array.from(new Set(arrays));
    }

    const chunks = raw
      .split(/(?:\r?\n)\s*---+\s*(?:\r?\n)|\r?\n{2,}/)
      .map((chunk) => normalizeCookiePayload(chunk))
      .filter(Boolean);
    if (chunks.length) {
      return chunks;
    }

    const single = normalizeCookiePayload(raw);
    return single ? [single] : [];
  };

  const runCookieAutodetect = async (rawCookieText) => {
    const text = normalizeCookiePayload(rawCookieText);
    const seq = detectRequestSeqRef.current + 1;
    detectRequestSeqRef.current = seq;

    if (!text) {
      setDetectingAccount(false);
      setDetectedAccount(null);
      setDetectError("");
      return;
    }

    setDetectingAccount(true);
    setDetectError("");
    try {
      const response = await apiFetchJson("/api/accounts/detect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Passing the selected proxy lets the backend resolve the real account
        // name (the web token no longer carries it) instead of showing an ID.
        body: JSON.stringify({ cookieText: text, proxyId: selectedProxy || "" }),
        timeoutMs: 90000,
        allowBaseFallback: true
      });
      if (seq !== detectRequestSeqRef.current) return;
      setDetectedAccount(response?.detected || null);
      setDetectError("");
    } catch (error) {
      if (seq !== detectRequestSeqRef.current) return;
      setDetectedAccount(null);
      setDetectError(error?.message || t("Не удалось определить аккаунт по cookies."));
    } finally {
      if (seq === detectRequestSeqRef.current) {
        setDetectingAccount(false);
      }
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    let timerId;
    let cancelled = false;

    const detectFromFile = async () => {
      if (files.length !== 1) {
        runCookieAutodetect("");
        return;
      }
      setDetectingAccount(true);
      setDetectError("");
      try {
        const text = await files[0].text();
        if (cancelled) return;
        await runCookieAutodetect(text);
      } catch (error) {
        if (cancelled) return;
        setDetectingAccount(false);
        setDetectedAccount(null);
        setDetectError(t("Не удалось прочитать файл cookies."));
      }
    };

    if (uploadMode === "file") {
      detectFromFile();
    } else {
      const textEntries = splitCookieTextEntries(cookieText);
      if (textEntries.length !== 1) {
        runCookieAutodetect("");
        return () => {
          cancelled = true;
        };
      }
      timerId = window.setTimeout(() => {
        runCookieAutodetect(textEntries[0]);
      }, 450);
    }

    return () => {
      cancelled = true;
      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
    // selectedProxy is a dep so that choosing a proxy after loading the cookies
    // re-runs detection and resolves the real name via the backend profile fetch.
  }, [isOpen, uploadMode, files, cookieText, selectedProxy]);

  // Файлы добавляются к уже выбранным, без повторов: cookies нескольких
  // аккаунтов можно набрать за пару заходов, лишний — убрать крестиком.
  const addCookieFiles = (incoming) => {
    const list = (Array.isArray(incoming) ? incoming : []).filter(Boolean);
    if (!list.length) return;
    setFiles((prev) => {
      const seen = new Set(prev.map((file) => `${file.name}:${file.size}`));
      const fresh = list.filter((file) => {
        const key = `${file.name}:${file.size}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      return fresh.length ? [...prev, ...fresh] : prev;
    });
  };
  const handleFileChange = (e) => {
    addCookieFiles(Array.from(e.target.files || []));
    // Тот же файл можно выбрать снова, если его убрали.
    e.target.value = "";
  };

  const updateRegistrationField = (field, value) => {
    setRegistrationForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    if (loading || submitInFlightRef.current) return;
    if (!selectedProxy) {
      alert(t("Выберите прокси для регистрации"));
      return;
    }

    const email = String(registrationForm.email || "").trim();
    const password = String(registrationForm.password || "");
    const contactName = String(registrationForm.contactName || "").trim();

    submitInFlightRef.current = true;
    setLoading(true);
    try {
      if (!registrationSessionId) {
        if (!email || !password || !contactName) {
          alert(t("Заполните email, пароль и имя профиля"));
          return;
        }
        const result = await apiFetchJson("/api/accounts/register/start", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            proxyId: selectedProxy,
            email,
            password,
            contactName,
            accountType: registrationForm.accountType
          }),
          timeoutMs: 180000,
          allowBaseFallback: true
        });
        setRegistrationSessionId(result.sessionId || "");
        setRegistrationStage("confirm");
        setRegistrationNotice(result.message || t("Регистрация отправлена. Вставьте ссылку из письма."));
        return;
      }

      if (registrationStage === "confirm") {
        const confirmationUrl = String(registrationForm.confirmationUrl || "").trim();
        if (!confirmationUrl) {
          alert(t("Вставьте ссылку подтверждения из письма"));
          return;
        }
        const result = await apiFetchJson("/api/accounts/register/confirm-link", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId: registrationSessionId,
            confirmationUrl,
            phoneNationalNumber: registrationForm.phoneNationalNumber,
            countryCallingCode: 49
          }),
          timeoutMs: 180000,
          allowBaseFallback: true
        });
        const requestedPhone = String(registrationForm.phoneNationalNumber || "").replace(/[^\d]/g, "");
        if (requestedPhone && result?.phoneVerificationStarted) {
          setRegistrationStage("phoneCode");
          setRegistrationNotice(result.message || t("SMS-код отправлен. Введите код из сообщения."));
        } else {
          setRegistrationStage("finalize");
          setRegistrationNotice(result.message || t("Email-ссылка открыта. Теперь можно выполнить вход и добавить аккаунт."));
        }
        return;
      }

      if (registrationStage === "phoneCode") {
        const phoneCode = String(registrationForm.phoneCode || "").replace(/[^\d]/g, "");
        if (phoneCode.length < 4) {
          alert(t("Введите корректный SMS-код телефона"));
          return;
        }
        const result = await apiFetchJson("/api/accounts/register/confirm-phone", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId: registrationSessionId,
            code: phoneCode
          }),
          timeoutMs: 120000,
          allowBaseFallback: true
        });
        setRegistrationStage("finalize");
        setRegistrationNotice(result.message || t("Телефон подтвержден. Теперь можно выполнить вход и добавить аккаунт."));
        return;
      }

      const result = await apiFetchJson("/api/accounts/register/finalize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId: registrationSessionId }),
        timeoutMs: 240000,
        allowBaseFallback: true
      });
      if (result?.success && result?.account) {
        onSuccess(result.account, result.metrics || null);
        alert(result.message || t("Аккаунт зарегистрирован и добавлен"));
        onClose();
      } else {
        alert(result?.error || t("Не удалось завершить регистрацию"));
      }
    } catch (error) {
      alert(error?.message || t("Не удалось выполнить регистрацию"));
    } finally {
      submitInFlightRef.current = false;
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (accountAddMode === "registration") {
      await handleRegistrationSubmit(e);
      return;
    }
    if (loading || submitInFlightRef.current) return;
    const uploadItems = [];
    if (uploadMode === "file") {
      if (!files.length) {
        alert(t("Пожалуйста, выберите хотя бы один файл"));
        return;
      }
      files.forEach((sourceFile, index) => {
        uploadItems.push({
          type: "file",
          sourceName: sourceFile?.name || `cookies-${index + 1}.txt`,
          sourceFile
        });
      });
    } else {
      const textEntries = splitCookieTextEntries(cookieText);
      if (!textEntries.length) {
        alert(t("Пожалуйста, вставьте куки"));
        return;
      }
      textEntries.forEach((entry, index) => {
        uploadItems.push({
          type: "text",
          sourceName: `cookies-${index + 1}.txt`,
          payloadText: entry
        });
      });
    }

    if (!selectedProxy) {
      alert(t("Выберите прокси для аккаунта"));
      return;
    }

    const buildFormDataForItem = (item) => {
      const formData = new FormData();
      if (item.type === "file" && item.sourceFile) {
        formData.append("cookieFile", item.sourceFile, item.sourceName);
      } else {
        const blob = new Blob([normalizeCookiePayload(item.payloadText)], { type: "text/plain" });
        formData.append("cookieFile", blob, item.sourceName);
      }
      formData.append("proxyId", selectedProxy);
      return formData;
    };

    const uploadSingleItem = async (item) => {
      const requestId = `acc-upload-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      return apiFetchJson("/api/accounts/upload", {
        method: "POST",
        body: buildFormDataForItem(item),
        timeoutMs: 240000,
        allowBaseFallback: true,
        requestId
      });
    };

    submitInFlightRef.current = true;
    setLoading(true);
    setUploadProgress({ current: 0, total: uploadItems.length });
    const uploaded = [];
    const failed = [];

    try {
      // Bulk upload runs as a worker pool with bounded concurrency:
      // each worker pulls the next pending item from a shared cursor
      // and POSTs it independently. Upload of N accounts now takes
      // roughly ceil(N / CONCURRENCY) × per-upload time instead of
      // N × per-upload time. Проверка куки на сервере — обычные HTTP-запросы
      // без браузера и без своей очереди, так что 10 аккаунтов за проход
      // сервер держит; больше — уже заметная пачка с одного прокси.
      const CONCURRENCY = 10;
      let nextIndex = 0;
      let completedCount = 0;

      const processItem = async (item) => {
        try {
          const result = await uploadSingleItem(item);
          if (result?.success && result?.account) {
            uploaded.push({
              account: result.account,
              metrics: result.metrics || null,
              sourceName: item.sourceName
            });
          } else {
            failed.push({
              sourceName: item.sourceName,
              error: result?.error || t("Неизвестная ошибка")
            });
          }
        } catch (error) {
          // Адреса — только когда ответа не было вовсе (обрыв сети): по ним видно,
          // куда не достучались. Ответ сервера с причиной («Куки невалидны»)
          // понятен и без них.
          const details = [];
          if (!error?.status) {
            if (error?.url) details.push(error.url);
            if (typeof window !== "undefined" && window.location?.origin) details.push(window.location.origin);
          }
          const suffix = details.length ? ` (${details.join(", ")})` : "";
          failed.push({
            sourceName: item.sourceName,
            error: (error?.message || "Failed to fetch") + suffix
          });
        } finally {
          completedCount += 1;
          setUploadProgress({ current: completedCount, total: uploadItems.length });
        }
      };

      const workerCount = Math.max(1, Math.min(CONCURRENCY, uploadItems.length));
      const workers = Array.from({ length: workerCount }, async () => {
        while (true) {
          const myIndex = nextIndex;
          nextIndex += 1;
          if (myIndex >= uploadItems.length) return;
          await processItem(uploadItems[myIndex]);
        }
      });
      await Promise.all(workers);

      if (uploaded.length) {
        if (uploaded.length > 1 && typeof onBulkSuccess === "function") {
          onBulkSuccess(uploaded);
        } else {
          uploaded.forEach((entry) => onSuccess(entry.account, entry.metrics || null));
        }
      }

      // Причина — одной фразой, если у всех неудачных она одна (обычно «Куки
      // невалидны»): «Не удалось добавить аккаунты (1/1). Куки невалидны.»
      // Разные — списком по файлам.
      const describeFailures = (limit, separator = "") => {
        const reasons = [...new Set(failed.map((item) => String(item.error || "").trim()))];
        if (reasons.length === 1) {
          const reason = reasons[0];
          if (!reason) return "";
          return `${separator} ${/[.!?…]$/.test(reason) ? reason : `${reason}.`}`;
        }
        return `\n${failed.slice(0, limit).map((item) => `• ${item.sourceName}: ${item.error}`).join("\n")}`;
      };

      if (uploaded.length && failed.length === 0) {
        alert(tf`Добавлено аккаунтов: ${uploaded.length}`);
        onClose();
      } else if (uploaded.length && failed.length > 0) {
        alert(tf`Добавлено ${uploaded.length} из ${uploadItems.length}.\nОшибок: ${failed.length}${describeFailures(2, ".")}`);
        onClose();
      } else {
        alert(tf`Не удалось добавить аккаунты (${failed.length}/${uploadItems.length}).${describeFailures(3)}`);
      }
    } finally {
      submitInFlightRef.current = false;
      setLoading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  // Положение считается до отрисовки (layout effect), чтобы список не мелькал не
  // там, и пересчитывается при прокрутке формы и смене размера окна: он стоит
  // поверх карточки, а не в ней, и сам за полем не едет. Если поле уехало за
  // край формы, список закрывается — висеть отдельно от поля ему незачем.
  useLayoutEffect(() => {
    if (!isOpen || !proxyDropdownOpen || typeof window === "undefined") return undefined;
    const place = () => {
      const trigger = proxyDropdownRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      let visibleTop = 0;
      let visibleBottom = window.innerHeight;
      for (let node = trigger.parentElement; node && node !== document.body; node = node.parentElement) {
        // Узел с display: contents (колонки окна на телефоне) своей рамки не
        // имеет — его нулевой прямоугольник иначе «закрывал» список сразу.
        const nodeStyle = window.getComputedStyle(node);
        if (nodeStyle.overflowY === "visible" || nodeStyle.display === "contents") continue;
        const box = node.getBoundingClientRect();
        visibleTop = Math.max(visibleTop, box.top);
        visibleBottom = Math.min(visibleBottom, box.bottom);
      }
      if (rect.bottom <= visibleTop || rect.top >= visibleBottom) {
        setProxyDropdownOpen(false);
        return;
      }
      const gap = 8;
      const edge = 12; // отступ от края экрана
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom - gap - edge;
      const spaceAbove = rect.top - gap - edge;
      const openUp = spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;
      const space = openUp ? spaceAbove : spaceBelow;
      setProxyDropdownPlacement({
        left: rect.left,
        width: rect.width,
        up: openUp,
        top: openUp ? null : rect.bottom + gap,
        bottom: openUp ? viewportHeight - rect.top + gap : null,
        maxHeight: Math.max(DROPDOWN_MIN_HEIGHT, Math.min(DROPDOWN_MAX_HEIGHT, Math.floor(space)))
      });
    };
    place();
    let frame = 0;
    const schedule = (event) => {
      // Прокрутка самого списка его положения не меняет.
      // У resize источник — window, а не узел, и contains на нём падает.
      const source = event?.target;
      if (source instanceof Node && proxyDropdownListRef.current?.contains(source)) return;
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        place();
      });
    };
    window.addEventListener("resize", schedule);
    document.addEventListener("scroll", schedule, true);
    return () => {
      window.removeEventListener("resize", schedule);
      document.removeEventListener("scroll", schedule, true);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [isOpen, proxyDropdownOpen]);

  useEffect(() => {
    if (!isOpen || !proxyDropdownOpen) return undefined;
    // Список живёт вне поля (в слое подложки), поэтому нажатие в нём — тоже
    // «внутри»: иначе он закрывался на mousedown, и выбор прокси не срабатывал.
    const handleOutsideClick = (event) => {
      if (proxyDropdownRef.current?.contains(event.target)) return;
      if (proxyDropdownListRef.current?.contains(event.target)) return;
      setProxyDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, proxyDropdownOpen]);

  const selectedProxyMeta = availableProxies.find((proxy) => String(proxy.id) === String(selectedProxy)) || null;

  if (!isOpen) return null;

  const textCookieEntries = uploadMode === "text" ? splitCookieTextEntries(cookieText) : [];
  const batchEntriesCount = uploadMode === "file" ? files.length : textCookieEntries.length;
  const isBatchMode = batchEntriesCount > 1;
  const isRegistration = accountAddMode === "registration";

  // Что определилось по cookies — строкой «Аккаунт» справа.
  const detectState = detectingAccount
    ? "loading"
    : detectError
      ? "error"
      : isBatchMode
        ? "batch"
        : detectedAccount
          ? (detectedAccount.validCandidate ? "ok" : "partial")
          : "idle";
  const detectedName = String(detectedAccount?.accountLabel || "").trim();

  const removeFileAt = (index) => setFiles((prev) => prev.filter((_, i) => i !== index));
  const fileDropProps = {
    onDragOver: (event) => { event.preventDefault(); if (!fileDropOver) setFileDropOver(true); },
    onDragLeave: () => setFileDropOver(false),
    onDrop: (event) => {
      event.preventDefault();
      setFileDropOver(false);
      addCookieFiles(Array.from(event.dataTransfer?.files || []));
    }
  };

  const submitLabel = isRegistration
    ? !registrationSessionId
      ? t("Начать регистрацию")
      : registrationStage === "phoneCode"
        ? t("Подтвердить SMS-код")
        : registrationStage === "finalize"
          ? t("Войти и добавить")
          : t("Открыть email-ссылку")
    : batchEntriesCount > 1
      ? tf`Добавить аккаунты: ${batchEntriesCount}`
      : t("Добавить аккаунт");

  const renderCookieFiles = () => (
    <div className={`nf-card aa-files${fileDropOver ? " is-over" : ""}`} {...fileDropProps}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".txt,.json,.cookies"
        multiple
        onChange={handleFileChange}
        style={{ display: "none" }}
      />
      {files.length ? (
        <>
          <div className="aa-file-list">
            {files.map((item, index) => (
              <div key={`${item.name}-${item.size}-${index}`} className="aa-file">
                <span className="aa-file-ico"><FileIcon size={16} /></span>
                <span className="aa-file-text">
                  <b>{item.name}</b>
                  <span>{(Number(item.size || 0) / 1024).toFixed(1)} KB</span>
                </span>
                <button type="button" className="ui-icon-btn aa-file-remove" onClick={() => removeFileAt(index)} title={t("Убрать файл")} aria-label={t("Убрать файл")}>
                  <XIcon size={14} />
                </button>
              </div>
            ))}
          </div>
          {/* Свободное место карточки — тоже поле для файлов. */}
          <button type="button" className="aa-drop is-more" onClick={() => fileInputRef.current?.click()}>
            <span className="aa-drop-ico"><PlusIcon size={16} /></span>
            <span className="aa-drop-sub">{isPhoneView ? t("Ещё файлы") : t("Ещё файлы — перетащите или нажмите")}</span>
          </button>
          <div className="aa-files-foot">
            <span>{tf`Файлов: ${files.length}`}{files.length > 1 ? ` · ${t("добавятся все")}` : ""}</span>
          </div>
        </>
      ) : (
        <button type="button" className="aa-drop" onClick={() => fileInputRef.current?.click()}>
          <span className="aa-drop-ico"><UploadIcon size={22} /></span>
          <span className="aa-drop-title">{t("Перетащите файлы cookies")}</span>
          <span className="aa-drop-sub">
            {isPhoneView ? t(".txt, .json, .cookies · можно несколько") : t("или нажмите, чтобы выбрать · .txt, .json, .cookies · можно несколько — добавятся все")}
          </span>
        </button>
      )}
    </div>
  );

  const renderCookieText = () => (
    <div className="nf-card nf-doc aa-text">
      <div className="aa-card-head">
        <span>{t("Текст cookies")}</span>
        {textCookieEntries.length > 1 && <span className="adf-meta">{tf`Блоков: ${textCookieEntries.length}`}</span>}
      </div>
      <textarea
        className="nf-plain aa-cookie-text"
        value={cookieText}
        onChange={(e) => setCookieText(e.target.value)}
        placeholder={t("Вставьте содержимое файла cookies — JSON или строку. Несколько аккаунтов — блоками подряд.")}
        aria-label={t("Текст cookies")}
        spellCheck={false}
      />
    </div>
  );

  const renderRegistration = () => (
    <div className="nf-card aa-reg">
      <div className="aa-grid">
        <label className="adf-param">
          <span className="adf-sublabel">Email</span>
          <input
            type="email"
            className="adf-input"
            value={registrationForm.email}
            onChange={(e) => updateRegistrationField("email", e.target.value)}
            placeholder="name@example.com"
            disabled={Boolean(registrationSessionId)}
          />
        </label>
        <label className="adf-param">
          <span className="adf-sublabel">{t("Пароль")}</span>
          <input
            type="password"
            className="adf-input"
            value={registrationForm.password}
            onChange={(e) => updateRegistrationField("password", e.target.value)}
            placeholder={t("Минимум 8 символов")}
            disabled={Boolean(registrationSessionId)}
          />
        </label>
        <label className="adf-param">
          <span className="adf-sublabel">{t("Имя профиля")}</span>
          <input
            type="text"
            className="adf-input"
            value={registrationForm.contactName}
            onChange={(e) => updateRegistrationField("contactName", e.target.value)}
            placeholder="Max Mustermann"
            disabled={Boolean(registrationSessionId)}
          />
        </label>
        <div className="adf-param">
          <span className="adf-sublabel">{t("Тип аккаунта")}</span>
          <StyledSelect
            value={registrationForm.accountType}
            onChange={(next) => updateRegistrationField("accountType", next)}
            disabled={Boolean(registrationSessionId)}
            options={[
              { value: "private", label: "Privat" },
              { value: "gewerblich", label: "Gewerblich" }
            ]}
            ariaLabel={t("Тип аккаунта")}
          />
        </div>
      </div>
      {registrationSessionId && (
        <>
          <label className="adf-param">
            <span className="adf-sublabel">{t("Ссылка из письма")}</span>
            <textarea
              className="adf-input"
              value={registrationForm.confirmationUrl}
              onChange={(e) => updateRegistrationField("confirmationUrl", e.target.value)}
              placeholder="https://www.kleinanzeigen.de/m-benutzer-verifizieren.html?uuid=..."
              disabled={registrationStage !== "confirm"}
              rows={3}
            />
          </label>
          <label className="adf-param">
            <span className="adf-sublabel">{t("Телефон DE без +49 (опционально)")}</span>
            <input
              type="text"
              className="adf-input"
              value={registrationForm.phoneNationalNumber}
              onChange={(e) => updateRegistrationField("phoneNationalNumber", e.target.value)}
              placeholder="17612345678"
              disabled={registrationStage !== "confirm"}
            />
          </label>
          {registrationStage === "phoneCode" && (
            <label className="adf-param">
              <span className="adf-sublabel">{t("SMS-код телефона")}</span>
              <input
                type="text"
                className="adf-input"
                value={registrationForm.phoneCode}
                onChange={(e) => updateRegistrationField("phoneCode", e.target.value.replace(/[^\d]/g, "").slice(0, 8))}
                placeholder="123456"
                inputMode="numeric"
                autoComplete="one-time-code"
              />
            </label>
          )}
        </>
      )}
      <div className="nf-hint">
        {registrationNotice || (
          registrationSessionId
            ? registrationStage === "phoneCode"
              ? t("Введите SMS-код, который пришел на указанный телефон.")
              : t("Вставьте ссылку подтверждения из письма и продолжите.")
            : t("Сначала отправляется регистрация, затем вставляется email-ссылка. Если указан телефон, следующим шагом будет ввод SMS-кода.")
        )}
      </div>
    </div>
  );

  return (
    <div ref={overlayRef} className="modal-overlay add-account-modal-root" {...overlayMotionProps(dialogState)} style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `radial-gradient(circle at 15% 10%, ${aa(700, 0.26)}, rgba(8,9,12,0.92) 46%, rgba(8,9,12,0.98) 100%)`,
      backdropFilter: "blur(10px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: isPhoneView
        ? "calc(14px + env(safe-area-inset-top, 0px)) 12px calc(14px + env(safe-area-inset-bottom, 0px))"
        : "20px"
    }}>
      <div className={`modal-card add-account-modal-card adf-shell nf aa-shell${isPhoneView ? " is-phone" : ""}`} data-modal-scroll="1" {...dialogMotionProps(dialogState)} style={{
        padding: isPhoneView ? "0 12px" : 0,
        width: isPhoneView ? "720px" : "900px",
        maxWidth: "100%",
        maxHeight: isPhoneView ? "100%" : "90vh",
        // На телефоне прокручивается всё окно, кнопка липнет книзу; на столе
        // прокручиваются колонки, шапка стоит.
        overflowY: isPhoneView ? "auto" : "hidden",
        overflowX: "hidden",
        overscrollBehavior: "contain",
        WebkitOverflowScrolling: "touch",
        display: "flex",
        flexDirection: "column"
      }}>
        {/* Шапка: название, способ (файл / текст), крестик. */}
        <div className="nf-head">
          <div className="nf-head-top">
            <div className="nf-title">
              <h2>{t("Добавить аккаунт")}</h2>
              <span className="aa-sub">{t("Cookies Kleinanzeigen — текстом или файлом")}</span>
            </div>
            {isPhoneView && (
              <button type="button" className="ui-icon-btn nf-close" onClick={onClose} aria-label={t("Закрыть")} title={t("Закрыть")}>
                <XIcon size={18} />
              </button>
            )}
          </div>
          {!isRegistration && (
            <div className="adf-seg aa-modes" role="radiogroup" aria-label={t("Способ добавления")}>
              {[
                { value: "text", label: t("Текст"), Icon: EditIcon },
                { value: "file", label: t("Файл"), Icon: FileIcon }
              ].map((mode) => {
                const active = uploadMode === mode.value;
                return (
                  <button
                    key={mode.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={`adf-seg-btn${active ? " is-on" : ""}`}
                    onClick={() => { setAccountAddMode("cookies"); setUploadMode(mode.value); }}
                  >
                    <mode.Icon size={14} /> {mode.label}
                  </button>
                );
              })}
            </div>
          )}
          {!isPhoneView && (
            <button type="button" className="ui-icon-btn nf-close" onClick={onClose} aria-label={t("Закрыть")} title={t("Закрыть")}>
              <XIcon size={18} />
            </button>
          )}
        </div>

        <form className="nf-body" onSubmit={handleSubmit}>
          <div className="nf-col nf-col--main">
            {isRegistration ? renderRegistration() : uploadMode === "file" ? renderCookieFiles() : renderCookieText()}
            {!isRegistration && detectState === "error" && (
              <div className="nf-hint is-bad aa-detect-error">{detectError}</div>
            )}
          </div>

          <div className="nf-col nf-col--side">
            <div className="nf-side-scroll">
              {/* Аккаунт — что определилось по cookies; прокси — выбором. */}
              <div className="nf-card nf-list">
                {!isRegistration && (
                  <div className={`nf-row aa-row-static aa-detect is-${detectState}`}>
                    <span className="nf-ico is-avatar">
                      {detectState === "loading"
                        ? <Spinner size={16} />
                        : (detectState === "ok" || detectState === "partial") && detectedName
                          ? detectedName.charAt(0).toUpperCase()
                          : <UserIcon size={18} />}
                    </span>
                    <span className="nf-row-text">
                      <span className="nf-row-label">{t("Аккаунт")}</span>
                      {detectState === "loading" ? (
                        <span className="nf-row-value is-empty">{t("Определяем по cookies...")}</span>
                      ) : detectState === "error" ? (
                        <span className="nf-row-value is-bad" title={detectError}>{t("Не удалось определить")}</span>
                      ) : detectState === "batch" ? (
                        <span className="nf-row-value">{tf`Массовый импорт: ${batchEntriesCount}`}</span>
                      ) : detectState === "ok" || detectState === "partial" ? (
                        <span className="nf-row-value" title={[detectedName, detectedAccount?.profileEmail].filter(Boolean).join(" · ")}>
                          {detectedName || t("Без имени")}
                          {detectedAccount?.profileEmail ? <small> · {detectedAccount.profileEmail}</small> : null}
                        </span>
                      ) : (
                        <span className="nf-row-value is-empty">{t("Определится по cookies")}</span>
                      )}
                    </span>
                    {detectState === "ok" && <span className="aa-badge is-ok">{t("Найден")}</span>}
                    {detectState === "partial" && <span className="aa-badge is-warn">{t("Частично")}</span>}
                  </div>
                )}

                <div ref={proxyDropdownRef} className={`aa-proxy${proxyDropdownOpen ? " is-open" : ""}`}>
                  <button
                    type="button"
                    className={`nf-row${proxyDropdownOpen ? " is-open" : ""}`}
                    aria-haspopup="listbox"
                    aria-expanded={proxyDropdownOpen}
                    onClick={() => setProxyDropdownOpen((prev) => !prev)}
                  >
                    <span className="nf-ico"><GlobeIcon size={18} /></span>
                    <span className="nf-row-text">
                      <span className="nf-row-label">{t("Прокси")}</span>
                      {selectedProxyMeta ? (
                        <span className="nf-row-value">
                          {selectedProxyMeta.name || "Proxy"}
                          {!selectedProxyMeta.isBuiltin && <small>{` · ${selectedProxyMeta.host}:${selectedProxyMeta.port}`}</small>}
                        </span>
                      ) : (
                        <span className="nf-row-value is-empty">{t("Выберите прокси")}</span>
                      )}
                    </span>
                    {selectedProxyMeta?.isBuiltin && <span className="ui-select-tag">{t("Встроенный")}</span>}
                    <ChevronRightIcon size={16} className="nf-chev" />
                  </button>

                  {proxyDropdownOpen && proxyDropdownPlacement && overlayRef.current && createPortal(
                    // Пометка для защиты жестов окна: без неё на телефоне свайп по
                    // списку гасился, и длинный список прокси не листался.
                    // Слой подложки, а не document.body: там действуют светлая тема
                    // окна (.add-account-modal-root) и его защита жестов.
                    <div
                      ref={proxyDropdownListRef}
                      role="listbox"
                      data-modal-scroll="1"
                      className={`ui-select-menu is-floating${proxyDropdownPlacement.up ? " is-up" : ""}`}
                      style={{
                        top: proxyDropdownPlacement.up ? "auto" : `${proxyDropdownPlacement.top}px`,
                        bottom: proxyDropdownPlacement.up ? `${proxyDropdownPlacement.bottom}px` : "auto",
                        left: `${proxyDropdownPlacement.left}px`,
                        width: `${proxyDropdownPlacement.width}px`,
                        maxHeight: `${proxyDropdownPlacement.maxHeight}px`
                      }}
                    >
                      {availableProxies.length === 0 ? (
                        <div className="ui-select-empty">{t("Нет доступных прокси")}</div>
                      ) : availableProxies.map((proxy) => {
                        const isSelected = String(proxy.id) === String(selectedProxy);
                        return (
                          <button
                            key={proxy.id}
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            className={`ui-select-option ui-select-option--hover${isSelected ? " is-selected" : ""}`}
                            onClick={() => {
                              setSelectedProxy(String(proxy.id));
                              setProxyDropdownOpen(false);
                            }}
                          >
                            <span className="ui-select-option-text">
                              <span className="ui-select-option-label">
                                {proxy.name || "Proxy"}
                                {proxy.isBuiltin && <span className="ui-select-tag">{t("Встроенный")}</span>}
                              </span>
                              <span className="ui-select-option-sub">
                                {proxy.isBuiltin
                                  ? (proxy.checkResult?.location?.country
                                      ? `${proxy.checkResult.location.country}${proxy.checkResult.location.city ? " · " + proxy.checkResult.location.city : ""}`
                                      : t("Готов к использованию"))
                                  : `${proxy.host}:${proxy.port}`}
                              </span>
                            </span>
                            {isSelected && (
                              <span className="ui-select-check"><CheckIcon size={15} /></span>
                            )}
                          </button>
                        );
                      })}
                    </div>,
                    overlayRef.current
                  )}
                </div>
              </div>
              {!isRegistration && (
                <div className="nf-hint aa-side-hint">
                  {t("Через выбранный прокси аккаунт будет работать с Kleinanzeigen — его же стоит указать, чтобы имя определилось точно.")}
                </div>
              )}
            </div>

            {/* Отправка — одна кнопка: «Отмена» не нужна, закрывает крестик.
                На телефоне она липнет к низу окна. */}
            <div className="nf-card nf-publish aa-submit">
              <button type="submit" className="ui-btn-primary kl-press-send nf-main-btn" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner size={16} />
                    {uploadProgress.total > 1
                      ? tf`Добавление ${Math.min(uploadProgress.current, uploadProgress.total)}/${uploadProgress.total}...`
                      : isRegistration
                        ? t("Регистрация...")
                        : t("Добавление...")}
                  </>
                ) : (
                  <><span className="kl-btn-ico"><SendIcon size={16} /></span> {submitLabel}</>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAccountModal;
